import React, { useMemo } from "react";

import {
  Form, Radio, Select, Tooltip,
} from "antd";
import { useWatch } from "antd/es/form/Form";

import EditableFields, {
  ENVIRONMENT_RESERVED_FIELD_KEYS,
  PRODUCT_RESERVED_FIELD_KEYS,
} from "../FieldsForm/Components/EditableFields";
import FixedValueInput from "../FieldsForm/Components/FixedValueInput";

import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../../Common/UIComponents/SelectPopupScrollbar";

import { ReactComponent as Info } from "../../../../../assets/images/onboarding/Info.svg";

import "../LinkReportForm.css";
import "../CompanyInfoForm.css";
import "./LocationSampleDetails.css";

function getJsonFieldName(str) {
  if (!str) return "";
  let jsonFieldName = str.trim().replace(/[^\w\s]/gi, " ").replace(/\s+/g, "_").toLowerCase();
  jsonFieldName = jsonFieldName.replace(/(?:__+)/g, "_");
  jsonFieldName = jsonFieldName.replace(/^_+|_+$/g, "");
  return jsonFieldName;
}

const FIXED_ENV_OPTIONS = [
  { value: "swab_number", label: "Swab Number" },
  { value: "zone", label: "Zone" },
  { value: "section", label: "Section" },
];

function fieldLabel(field) {
  if (typeof field === "string") return field.trim();
  return String(field?.title_field || field?.json_field || "").trim();
}

function optionsFromEditableFields(fieldList = []) {
  const seen = new Set();
  return fieldList.reduce((options, field) => {
    const label = fieldLabel(field);
    const value = getJsonFieldName(label);
    if (!label || !value || seen.has(value)) return options;
    seen.add(value);
    options.push({ value, label });
    return options;
  }, []);
}

export default function LocationSampleDetails({ type = "product" }) {
  const isProduct = type === "product";
  const supplyField = isProduct ? "sample_supply" : "env_sample_supply";
  const supplyValue = useWatch(supplyField);
  const sampling = supplyValue === "Yes";
  const editableFields = useWatch("editable_fields") || [];
  const linkOptions = useMemo(() => {
    const fromFields = optionsFromEditableFields(
      editableFields.length ? editableFields : (isProduct ? ["Item", "Lot", "Description"] : []),
    );
    if (isProduct) return fromFields;
    return [
      ...FIXED_ENV_OPTIONS,
      ...fromFields.filter((option) => !FIXED_ENV_OPTIONS.some((item) => item.value === option.value)),
    ];
  }, [editableFields, isProduct]);

  const title = isProduct ? "Product Details" : "Environment Details";
  const question = isProduct
    ? "Are you planning to send us product samples for testing?"
    : "Are you planning to send us environment samples for testing?";

  if (isProduct) {
    return (
      <div className="productDetail LocationSampleDetails">
        <div className="OnboardingFormTitle">{title}</div>
        <Form.Item
          name={supplyField}
          label={<span className="FieldsForm_Custom_TextLabel MyForm__SectionLabel">{question}</span>}
          rules={[{ required: true, message: "" }]}
        >
          <Radio.Group className="Onboarding__RadioGroup">
            <div className="Onboarding__RadioOption">
              <Radio value="Yes">Yes</Radio>
            </div>
            <div className="Onboarding__RadioOption">
              <Radio value="No">No</Radio>
            </div>
          </Radio.Group>
        </Form.Item>

        {sampling && (
          <div>
            <div className="FormsContainer_Divider" />
            <Form.Item label={(
              <span className="MyForm__SectionLabel FieldsForm__LabelText_Fields">
                Product Sample Submission Form Fields
                <Tooltip title="Info to track the samples you send to the lab.">
                  <Info />
                </Tooltip>
              </span>
            )}
            >
              <Form.Item name="sample_type" rules={[{ required: true, message: "" }]}>
                <FixedValueInput />
              </Form.Item>
              <Form.Item name="date" rules={[{ required: true, message: "" }]}>
                <FixedValueInput />
              </Form.Item>
              <Form.Item name="physical_category" rules={[{ required: true, message: "" }]}>
                <FixedValueInput />
              </Form.Item>
              <EditableFields
                getJsonFieldName={getJsonFieldName}
                removableFieldIndex={2}
                reservedFieldKeys={PRODUCT_RESERVED_FIELD_KEYS}
              />
              <div className="FormsContainer_Divider" />
              <Form.Item
                name="product_link"
                label={<span className="MyForm__SectionLabel">Select the field that is associated with the specification for your product :</span>}
                rules={[{ required: true, message: "" }]}
                className="LinkReportForm__ProductSpec"
              >
                <Select
                  mode="multiple"
                  allowClear
                  {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                  classNames={selectPopupClassNames()}
                  virtual={false}
                  listHeight={256}
                  popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                    optionCount: (linkOptions.length
                      ? linkOptions
                      : optionsFromEditableFields(editableFields)).length,
                  })}
                  options={linkOptions.length
                    ? linkOptions
                    : optionsFromEditableFields(editableFields)}
                  placeholder="Item Name"
                />
              </Form.Item>
            </Form.Item>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="productDetail LocationSampleDetails">
      <div className="OnboardingFormTitle">{title}</div>
      <Form.Item>
        <Form.Item
          name={supplyField}
          label={<span className="FieldsForm_Custom_TextLabel MyForm__SectionLabel">{question}</span>}
          rules={[{ required: true, message: "" }]}
        >
          <Radio.Group className="Onboarding__RadioGroup">
            <div className="Onboarding__RadioOption">
              <Radio value="Yes">Yes</Radio>
            </div>
            <div className="Onboarding__RadioOption">
              <Radio value="No">No</Radio>
            </div>
          </Radio.Group>
        </Form.Item>

        {sampling && (
          <div>
            <Form.Item label={<span className="MyForm__SectionLabel FieldsForm__LabelText">Please enter the details you would like to include while submitting Environment samples</span>}>
              <Form.Item label={(
                <span className="MyForm__SectionLabel FieldsForm__LabelText_Fields">
                  Environment Sample Submission Form Fields
                  <Tooltip title="Info to track the samples you send to the lab.">
                    <Info />
                  </Tooltip>
                </span>
              )}
              >
                <Form.Item name="sample_type" rules={[{ required: true, message: "" }]}>
                  <FixedValueInput />
                </Form.Item>
                <Form.Item name="date" rules={[{ required: true, message: "" }]}>
                  <FixedValueInput />
                </Form.Item>
                <Form.Item name="physical_category" rules={[{ required: true, message: "" }]}>
                  <FixedValueInput />
                </Form.Item>
                <Form.Item name="swab_number" rules={[{ required: true, message: "" }]}>
                  <FixedValueInput />
                </Form.Item>
                <Form.Item name="zone" rules={[{ required: true, message: "" }]}>
                  <FixedValueInput />
                </Form.Item>
                <Form.Item name="section" rules={[{ required: true, message: "" }]}>
                  <FixedValueInput />
                </Form.Item>
                <EditableFields
                  getJsonFieldName={getJsonFieldName}
                  fieldLimit={3}
                  reservedFieldKeys={ENVIRONMENT_RESERVED_FIELD_KEYS}
                />
                <div className="FormsContainer_Divider" />
                <Form.Item
                  name="environment_link"
                  label={<span className="MyForm__SectionLabel FieldsForm__LabelText">Please select the field(s) associated with the specification for your environment:</span>}
                  rules={[{ required: true, message: "" }]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                    classNames={selectPopupClassNames()}
                    virtual={false}
                    listHeight={256}
                    popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                      optionCount: (linkOptions || []).length,
                    })}
                    options={linkOptions}
                    placeholder="Select field(s)"
                  />
                </Form.Item>
                <div className="FormsContainer_Divider" />
                <Form.Item
                  name="environment_supplies_needed"
                  label={<span className="MyForm__SectionLabel FieldsForm__LabelText">Do you need supplies for environment testing?</span>}
                  rules={[{ required: true, message: "Please provide an answer" }]}
                >
                  <Radio.Group className="Onboarding__RadioGroup">
                    <div className="Onboarding__RadioOption">
                      <Radio value="Yes">Yes</Radio>
                    </div>
                    <div className="Onboarding__RadioOption">
                      <Radio value="No">No</Radio>
                    </div>
                  </Radio.Group>
                </Form.Item>
              </Form.Item>
            </Form.Item>
          </div>
        )}
      </Form.Item>
    </div>
  );
}
